import { apiUrl } from "./api";

export type PronunciationAccent = "uk" | "us";
export type AudioPlaybackState = "loading" | "playing" | "idle";

interface PlaybackAudio {
  onended: ((event: Event) => void) | null;
  onerror: ((event: Event | string) => void) | null;
  onplaying: ((event: Event) => void) | null;
  play(): Promise<void>;
  pause(): void;
  removeAttribute(name: string): void;
  load(): void;
}

export interface AudioPlaybackEnvironment {
  createAudio(path: string): PlaybackAudio;
}

export interface AudioPlaybackRequest {
  key: string;
  path: string;
  onState: (state: AudioPlaybackState) => void;
  onError: () => void;
}

interface ActivePlayback {
  request: AudioPlaybackRequest;
  audio: PlaybackAudio;
  state: Exclude<AudioPlaybackState, "idle">;
}

export class AudioPlaybackController {
  private active: ActivePlayback | null = null;

  constructor(private readonly environment: AudioPlaybackEnvironment) {}

  toggle(request: AudioPlaybackRequest): "loading" | "stopped" {
    if (this.active?.request.key === request.key) {
      this.stopActive();
      return "stopped";
    }
    this.stopActive();
    const audio = this.environment.createAudio(request.path);
    const active: ActivePlayback = {
      request,
      audio,
      state: "loading",
    };
    this.active = active;
    request.onState("loading");
    audio.onended = () => this.finish(active, false);
    audio.onerror = () => this.finish(active, true);
    audio.onplaying = () => this.markPlaying(active);
    void audio.play()
      .then(() => this.markPlaying(active))
      .catch(() => this.finish(active, true));
    return "loading";
  }

  stop(key?: string) {
    if (key && this.active?.request.key !== key) return;
    this.stopActive();
  }

  private stopActive() {
    const active = this.active;
    if (!active) return;
    this.active = null;
    active.audio.pause();
    active.audio.removeAttribute("src");
    active.audio.load();
    active.request.onState("idle");
  }

  private markPlaying(active: ActivePlayback) {
    if (this.active !== active || active.state === "playing") return;
    active.state = "playing";
    active.request.onState("playing");
  }

  private finish(active: ActivePlayback, failed: boolean) {
    if (this.active !== active) return;
    this.active = null;
    active.audio.pause();
    active.audio.removeAttribute("src");
    active.audio.load();
    active.request.onState("idle");
    if (failed) active.request.onError();
  }
}

let browserController: AudioPlaybackController | null = null;

export function getBrowserAudioController() {
  if (browserController) return browserController;
  browserController = new AudioPlaybackController({
    createAudio: (path) => {
      const audio = new Audio();
      audio.crossOrigin = "use-credentials";
      audio.preload = "auto";
      audio.src = apiUrl(path);
      return audio;
    },
  });
  return browserController;
}
